import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { customMetricsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Calculator, Target, Scale, FlaskConical } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const METRIC_TYPES = [
  { value: 'formula', label: 'Formula-Based', icon: Calculator, description: 'Create metrics using mathematical formulas' },
  { value: 'weighted_average', label: 'Weighted Average', icon: Scale, description: 'Combine metrics with custom weights' },
  { value: 'goal', label: 'Goal-Based', icon: Target, description: 'Track progress against targets' },
];

const FORMAT_TYPES = [
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency ($)' },
  { value: 'percentage', label: 'Percentage (%)' },
];

const COMPARISON_OPERATORS = [
  { value: 'gte', label: '>= (Greater than or equal)' },
  { value: 'lte', label: '<= (Less than or equal)' },
  { value: 'gt', label: '> (Greater than)' },
  { value: 'lt', label: '< (Less than)' },
  { value: 'eq', label: '= (Equal to)' },
];

const CustomMetrics = () => {
  const [metrics, setMetrics] = useState([]);
  const [baseMetrics, setBaseMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    label: '',
    description: '',
    metric_type: 'formula',
    // Formula
    formula_expression: '',
    // Weighted average
    weighted_components: [{ metric: 'impressions', weight: 1 }],
    // Goal
    base_metric: 'spend',
    goal_target: 0,
    goal_comparison: 'gte',
    goal_period: 'daily',
    // Display
    format_type: 'number',
    decimal_places: 2,
    show_on_dashboard: true,
    show_on_reports: true,
  });

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const [metricsRes, baseRes] = await Promise.all([
        customMetricsAPI.getAll(),
        customMetricsAPI.getBaseMetrics()
      ]);
      setMetrics(metricsRes.data || []);
      setBaseMetrics(baseRes.data?.metrics || []);
    } catch (error) {
      toast.error('Failed to load custom metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.label) {
      toast.error('Name and label are required');
      return;
    }

    try {
      const data = {
        name: formData.name.toLowerCase().replace(/\s+/g, '_'),
        label: formData.label,
        description: formData.description,
        metric_type: formData.metric_type,
        format_type: formData.format_type,
        decimal_places: formData.decimal_places,
        show_on_dashboard: formData.show_on_dashboard,
        show_on_reports: formData.show_on_reports,
      };

      if (formData.metric_type === 'formula') {
        if (!formData.formula_expression) {
          toast.error('Formula expression is required');
          return;
        }
        data.formula = { expression: formData.formula_expression };
      } else if (formData.metric_type === 'weighted_average') {
        if (formData.weighted_components.length === 0) {
          toast.error('At least one weighted component is required');
          return;
        }
        data.weighted_components = formData.weighted_components;
      } else if (formData.metric_type === 'goal') {
        data.base_metric = formData.base_metric;
        data.goal_config = {
          target_value: formData.goal_target,
          comparison: formData.goal_comparison,
          period: formData.goal_period,
        };
      }

      if (editingMetric) {
        await customMetricsAPI.update(editingMetric.id, data);
        toast.success('Custom metric updated');
      } else {
        await customMetricsAPI.create(data);
        toast.success('Custom metric created');
      }

      setDialogOpen(false);
      resetForm();
      fetchMetrics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save custom metric');
    }
  };

  const handleDelete = async (metricId) => {
    if (!confirm('Are you sure you want to delete this custom metric?')) return;

    try {
      await customMetricsAPI.delete(metricId);
      toast.success('Custom metric deleted');
      fetchMetrics();
    } catch (error) {
      toast.error('Failed to delete custom metric');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      label: '',
      description: '',
      metric_type: 'formula',
      formula_expression: '',
      weighted_components: [{ metric: 'impressions', weight: 1 }],
      base_metric: 'spend',
      goal_target: 0,
      goal_comparison: 'gte',
      goal_period: 'daily',
      format_type: 'number',
      decimal_places: 2,
      show_on_dashboard: true,
      show_on_reports: true,
    });
    setEditingMetric(null);
    setPreviewResult(null);
  };

  const openEditDialog = (metric) => {
    setEditingMetric(metric);
    setFormData({
      name: metric.name,
      label: metric.label,
      description: metric.description || '',
      metric_type: metric.metric_type,
      formula_expression: metric.formula?.expression || '',
      weighted_components: metric.weighted_components || [{ metric: 'impressions', weight: 1 }],
      base_metric: metric.base_metric || 'spend',
      goal_target: metric.goal_config?.target_value || 0,
      goal_comparison: metric.goal_config?.comparison || 'gte',
      goal_period: metric.goal_config?.period || 'daily',
      format_type: metric.format_type,
      decimal_places: metric.decimal_places,
      show_on_dashboard: metric.show_on_dashboard,
      show_on_reports: metric.show_on_reports,
    });
    setDialogOpen(true);
  };

  const addWeightedComponent = () => {
    setFormData(prev => ({
      ...prev,
      weighted_components: [...prev.weighted_components, { metric: 'clicks', weight: 1 }]
    }));
  };

  const removeWeightedComponent = (index) => {
    setFormData(prev => ({
      ...prev,
      weighted_components: prev.weighted_components.filter((_, i) => i !== index)
    }));
  };

  const updateWeightedComponent = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      weighted_components: prev.weighted_components.map((c, i) =>
        i === index ? { ...c, [field]: value } : c
      )
    }));
  };

  const previewFormula = async () => {
    if (!formData.formula_expression) return;
    
    try {
      const response = await customMetricsAPI.previewFormula(formData.formula_expression);
      setPreviewResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid formula');
      setPreviewResult(null);
    }
  };

  const getMetricTypeIcon = (type) => {
    const found = METRIC_TYPES.find(t => t.value === type);
    return found ? found.icon : Calculator;
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Custom Metrics</h1>
            <p className="text-sm text-slate-500 mt-1">
              Create formula-based, weighted average, and goal-based metrics
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="create-metric-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Metric
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingMetric ? 'Edit Custom Metric' : 'Create Custom Metric'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Metric Name (ID) *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        name: e.target.value.toLowerCase().replace(/\s+/g, '_')
                      }))}
                      placeholder="e.g., conversion_rate"
                      disabled={!!editingMetric}
                    />
                  </div>
                  <div>
                    <Label>Display Label *</Label>
                    <Input
                      value={formData.label}
                      onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="e.g., Conversion Rate"
                    />
                  </div>
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional description"
                    rows={2}
                  />
                </div>

                {/* Metric Type Selection */}
                <div>
                  <Label className="mb-3 block">Metric Type</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {METRIC_TYPES.map(type => (
                      <div
                        key={type.value}
                        onClick={() => setFormData(prev => ({ ...prev, metric_type: type.value }))}
                        className={`p-4 rounded-lg border cursor-pointer transition-all ${
                          formData.metric_type === type.value
                            ? 'border-slate-900 bg-slate-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <type.icon className={`w-6 h-6 mb-2 ${
                          formData.metric_type === type.value ? 'text-slate-900' : 'text-slate-400'
                        }`} />
                        <div className="font-medium text-sm">{type.label}</div>
                        <div className="text-xs text-slate-500 mt-1">{type.description}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Formula Type */}
                {formData.metric_type === 'formula' && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label>Formula Expression *</Label>
                      <div className="flex gap-2">
                        <Input
                          value={formData.formula_expression}
                          onChange={(e) => setFormData(prev => ({ ...prev, formula_expression: e.target.value }))}
                          placeholder="e.g., (conversions / clicks) * 100"
                          className="font-mono"
                        />
                        <Button type="button" variant="outline" onClick={previewFormula}>
                          <FlaskConical className="w-4 h-4 mr-1" />
                          Test
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Available variables: {baseMetrics.join(', ')}
                      </p>
                    </div>
                    
                    {previewResult && (
                      <div className="p-3 bg-white rounded border">
                        <div className="text-sm font-medium">Preview Result</div>
                        <div className="text-2xl font-bold text-slate-900">{previewResult.result}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          Using sample data: {Object.entries(previewResult.sample_data || {}).map(([k, v]) => `${k}=${v}`).join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Weighted Average Type */}
                {formData.metric_type === 'weighted_average' && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label>Weighted Components</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addWeightedComponent}>
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </div>
                    
                    {formData.weighted_components.map((comp, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-white rounded border">
                        <Select
                          value={comp.metric}
                          onValueChange={(value) => updateWeightedComponent(index, 'metric', value)}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {baseMetrics.map(m => (
                              <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-slate-500">×</span>
                        <Input
                          type="number"
                          value={comp.weight}
                          onChange={(e) => updateWeightedComponent(index, 'weight', parseFloat(e.target.value) || 0)}
                          className="w-24"
                          step="0.1"
                        />
                        {formData.weighted_components.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeWeightedComponent(index)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Goal Type */}
                {formData.metric_type === 'goal' && (
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Base Metric</Label>
                        <Select
                          value={formData.base_metric}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, base_metric: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {baseMetrics.map(m => (
                              <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Target Value</Label>
                        <Input
                          type="number"
                          value={formData.goal_target}
                          onChange={(e) => setFormData(prev => ({ ...prev, goal_target: parseFloat(e.target.value) || 0 }))}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Comparison</Label>
                        <Select
                          value={formData.goal_comparison}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, goal_comparison: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COMPARISON_OPERATORS.map(op => (
                              <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Period</Label>
                        <Select
                          value={formData.goal_period}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, goal_period: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="total">Total</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Display Settings */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Format</Label>
                    <Select
                      value={formData.format_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, format_type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FORMAT_TYPES.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Decimal Places</Label>
                    <Input
                      type="number"
                      value={formData.decimal_places}
                      onChange={(e) => setFormData(prev => ({ ...prev, decimal_places: parseInt(e.target.value) || 0 }))}
                      min={0}
                      max={6}
                    />
                  </div>
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.show_on_dashboard}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_on_dashboard: checked }))}
                    />
                    <span>Show on Dashboard</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.show_on_reports}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_on_reports: checked }))}
                    />
                    <span>Show on Reports</span>
                  </label>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingMetric ? 'Update' : 'Create'} Metric
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Metrics List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : metrics.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No custom metrics defined</p>
                <p className="text-sm mt-1">Create your first custom metric to get started</p>
              </div>
            ) : (
              <div className="divide-y">
                {metrics.map((metric) => {
                  const TypeIcon = getMetricTypeIcon(metric.metric_type);
                  return (
                    <div key={metric.id} className="p-4 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <TypeIcon className="w-5 h-5 text-slate-600" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">{metric.label}</div>
                        <div className="text-sm text-slate-500">
                          {metric.name} • {METRIC_TYPES.find(t => t.value === metric.metric_type)?.label} •{' '}
                          {FORMAT_TYPES.find(f => f.value === metric.format_type)?.label}
                        </div>
                        {metric.formula?.expression && (
                          <code className="text-xs bg-slate-100 px-2 py-0.5 rounded mt-1 inline-block">
                            {metric.formula.expression}
                          </code>
                        )}
                      </div>

                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        {metric.show_on_dashboard && (
                          <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded">Dashboard</span>
                        )}
                        {metric.show_on_reports && (
                          <span className="px-2 py-1 bg-green-50 text-green-600 rounded">Reports</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(metric)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(metric.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Formula Reference
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-600">
            <p className="mb-3">Available variables for formulas:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {baseMetrics.map(m => (
                <code key={m} className="bg-slate-100 px-2 py-1 rounded text-xs">{m}</code>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-500">
              Example formulas: <code>(conversions / clicks) * 100</code> for conversion rate,{' '}
              <code>revenue - spend</code> for profit
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default CustomMetrics;
